import OrderService from 'services/OrderService';

import { pivot } from 'utils';

import dateformat from 'dateformat';

const DERIVATIVES_ASSET_CLASSES = [
  "Interest Rate Derivatives",
  "Commodity derivatives",
  "Credit Derivatives",
  "Foreign Exchange Derivatives",
  "Commodity Derivatives",
  "Equity Derivatives",
  "Emission Allowance Derivatives"
];

const _formatDate = function (timestamp = NaN, format = "UTC:yyyy-mm-dd'T'HH:MM:ss:l") {
  const date = new Date(timestamp);

  const formattedDate = window.isNaN(date.getTime()) ? null : dateformat(date, format);
  return formattedDate;
};

const _formatDateWithoutTime = function (timestamp = NaN, format = "UTC:yyyy-mm-dd") {
  const date = new Date(timestamp);

  const formattedDate = window.isNaN(date.getTime()) ? null : dateformat(date, format);
  return formattedDate;
};

const ukVenueCode = 1;

const _formatCleared = (row) => {
  const assetClass = (row.AssetClass || '').trim();
  const isDerivative = DERIVATIVES_ASSET_CLASSES.some(
    d => d.toLowerCase() === assetClass.toLowerCase()
  );

  // Get venue code
  const venueCode = row.Message && row.Message.VenueLocation;

  const isUK = venueCode === ukVenueCode;
  const isEU = !isUK; // EU is any venue that is not UK

  // If Cleared is present, venue is EU (not UK), and asset class is a derivative, display Cleared value
  if (
    row.hasOwnProperty('Cleared') &&
    isEU &&
    isDerivative
  ) {
    return row.Cleared === true ? "True" : "False";
  }

  // For all other cases
  if (row.Cleared === true) {
    return "True";
  }
  return "";
};

const isEquity = (row, order) => (row.AssetClass || order.AssetClass) === "Equity & Equity-like";

const showNotAvailableIfEmpty = (value, row, order) => {
  if (!isEquity(row, order)) {
    return "";
  }
  return value === '' ? "N/A" : value;
};

const showNotAvailableIfEmptyForTrades = (value, row, order) => {
  return value === '' ? "N/A" : value;
};

const showZeroIfNull = (value, row, order) => {
  if (!isEquity(row, order)) {
    return "";
  }
  return (value == null || value === '' || value === 0) ? "N/A" : value;
};


const _defaultTransformer = function (data, order) {
  return data.map((row) => ({
    date: _formatDate(row.Date),
    price: row.Price, aggrVol:
      row.AggrVol,
    currency: row.Currency,
    venueMIC: row.VenueMIC,
    instrumentId: row.InstrumentID,
    instrumentDesc: row.InstrumentDescription,
    instrumentIdType: row.InstrumentIDType,
    assetClass: row.AssetClass || order.AssetClass,
    subAssetClass: row.SubAssetClass || order.SubAssetClass,
    viewType: order.ViewType,
    tradingVenue: order.TradingVenue,
    dataType: (order.DataType || '').toLowerCase(),
    messageType: order.MessageType,
    marketMaker: showNotAvailableIfEmpty(row.MarketMaker, row, order),
    tradingSystem: showNotAvailableIfEmpty(row.TradingSystem, row, order),
    tradingSystemPhase: showNotAvailableIfEmpty(row.TradingSystemPhase, row, order),
    aggregatedQuotesAndOrder: showZeroIfNull(row.AggregatedQuotesAndOrder, row, order)
  }));
};

const _transformers = {
  'ord-disp-clob': function (data, order) {
    return data.map((row) => ({
      lOrders: row.LOrders,
      rOrders: row.ROrders,
      bidPrice: row.BidPrice,
      aggrBidVol: row.AggrBidVol,
      askPrice: row.AskPrice,
      aggrAskVol: row.AggrAskVol,
      currency: row.Currency,
      venueMIC: row.VenueMIC,
      instrumentDesc: row.InstrumentDescription,
      instrumentId: row.InstrumentID,
      instrumentIdType: row.InstrumentIDType,
      assetClass: row.AssetClass || order.AssetClass,
      subAssetClass: row.SubAssetClass || order.SubAssetClass,
      viewType: order.ViewType,
      tradingVenue: order.TradingVenue,
      dataType: (order.DataType || '').toLowerCase(),
      messageType: order.MessageType,
      groupId: row.OrderID + "_" + row.InstrumentID + "_" + row.Timestamp,
      marketMakerL: row.LOrders ? showNotAvailableIfEmpty(row.MarketMakerL, row, order) : '',
      tradingSystemL: row.LOrders ? showNotAvailableIfEmpty(row.TradingSystemL, row, order) : '',
      tradingSystemPhaseL: row.LOrders ? showNotAvailableIfEmpty(row.TradingSystemPhaseL, row, order) : '',
      aggregatedQuotesAndOrderL: row.LOrders ? showZeroIfNull(row.AggregatedQuotesAndOrderL, row, order) : '',
      marketMakerR: row.ROrders ? showNotAvailableIfEmpty(row.MarketMakerR, row, order) : '',
      tradingSystemR: row.ROrders ? showNotAvailableIfEmpty(row.TradingSystemR, row, order) : '',
      tradingSystemPhaseR: row.ROrders ? showNotAvailableIfEmpty(row.TradingSystemPhaseR, row, order) : '',
      aggregatedQuotesAndOrderR: row.ROrders ? showZeroIfNull(row.AggregatedQuotesAndOrderR, row, order) : ''
    }));
  },

  'ord-disp-voice': function (data, order) {
    return data.map((row) => ({
      date: _formatDate(row.Date),
      orders: row.Orders,
      bidPrice: row.BidPrice,
      bidCurrency: row.BidCurrency,
      bidVol: row.BidVol,
      askPrice: row.AskPrice,
      askVol: row.AskVol,
      askCurrency: row.AskCurrency,
      currency: row.Currency,
      venueMIC: row.VenueMIC,
      instrumentId: row.InstrumentID,
      instrumentDesc: row.InstrumentDescription,
      instrumentIdType: row.InstrumentIDType,
      assetClass: row.AssetClass || order.AssetClass,
      subAssetClass: row.SubAssetClass || order.SubAssetClass,
      viewType: order.ViewType,
      tradingVenue: order.TradingVenue,
      dataType: (order.DataType || '').toLowerCase(),
      messageType: order.MessageType,
      marketMaker: showNotAvailableIfEmpty(row.MarketMaker, row, order),
      tradingSystem: showNotAvailableIfEmpty(row.TradingSystem, row, order),
      tradingSystemPhase: showNotAvailableIfEmpty(row.TradingSystemPhase, row, order),
      aggregatedQuotesAndOrder: showZeroIfNull(row.AggregatedQuotesAndOrder, row, order)
    }));
  },

  'ord-disp-rfq': _defaultTransformer,

  'ord-disp-auc': _defaultTransformer,

  'trade-disp': function (data, order) {
    return data.map((row) => ({
      tradeDate: _formatDate(row.TradeDate),
      price: row.Price,
      missingPrice: row.MissingPrice,
      vol: row.Vol === 0 ? '' : row.Vol,
      priceNot: row.PriceNot,
      publicationDate: _formatDate(row.PublicationDate),
      transactionID: row.TransactionID,
      notionalAmt: row.NotionalAmt,
      notionalCcy: row.NotionalCcy,
      flags: row.Flags.join(', '),
      status: row.Status,
      qtyMeasUnit: row.QtyMeasUnit === 0 ? '' : row.QtyMeasUnit,
      type: row.Type,
      cleared: _formatCleared(row),
      currency: row.Currency,
      thirdCountryExecutionVenue: row.ThirdCountryExecutionVenue,
      priceCondition: row.PriceCondition,
      priceMajorCurrency: row.PriceMajorCurrency,

      // Add new fields to the transformed data
      venueMIC: row.VenueMIC,
      assetClass: row.AssetClass || order.AssetClass,
      subAssetClass: row.SubAssetClass || order.SubAssetClass,
      tradingVenue: row.TradingVenue || order.TradingVenue,
      instrumentId: row.InstrumentID,
      instrumentDesc: row.InstrumentDescription,
      instrumentIdType: row.InstrumentIDType,
      viewType: order.ViewType,
      dataType: (order.DataType || '').toLowerCase(),
      messageType: order.MessageType,
      effectiveDateOfContract: row.EffectiveDateOfContract !== 0 ? _formatDateWithoutTime(row.EffectiveDateOfContract) : '',
      maturityDateOfContract: row.MaturityDateOfContract !== 0 ? _formatDateWithoutTime(row.MaturityDateOfContract) : '',
      spread: row.Spread,
      upfrontPayment: row.UpfrontPayment === 0.0 ? '' : row.UpfrontPayment,
      clearingHouseLEI: row.ClearingHouseLEI,
      totalNumberOfTransactions: row.TotalNumberOfTransactions === 0 ? '' : row.TotalNumberOfTransactions,
      isActual: row.isActual,
      tradingSystem: showNotAvailableIfEmptyForTrades(row.TradingSystem, row, order)
    }));
  },

  'trade-disp-2': function (data, order) {
    return pivot(
      data.map((row) => ({
        tradeDate: _formatDate(row.TradeDate),
        price: row.Price,
        missingPrice: row.MissingPrice,
        vol: row.Vol === 0 ? '' : row.Vol,
        ccy: row.Ccy,
        priceNot: row.PriceNot,
        publicationDate: _formatDate(row.PublicationDate),
        transactionID: row.TransactionID,
        notionalAmt: row.NotionalAmt,
        notionalCcy: row.NotionalCcy,
        flags: row.Flags.join(', '),
        status: row.Status,
        qtyMeasUnit: row.QtyMeasUnit === 0 ? '' : row.QtyMeasUnit,
        qtyPerMeas: row.QtyPerMeas,
        type: row.Type,
        cleared: _formatCleared(row),
        thirdCountryExecutionVenue: row.ThirdCountryExecutionVenue,
        // Add new fields to the transformed data
        venueMIC: row.VenueMIC,
        assetClass: row.AssetClass || order.AssetClass,
        subAssetClass: row.SubAssetClass || order.SubAssetClass,
        tradingVenue: row.TradingVenue || order.TradingVenue,
        instrumentId: row.InstrumentID,
        instrumentDesc: row.InstrumentDescription,
        instrumentIdType: row.InstrumentIDType,
        viewType: order.ViewType,
        dataType: (order.DataType || '').toLowerCase(),
        messageType: order.MessageType,
        effectiveDateOfContract: row.EffectiveDateOfContract !== 0 ? _formatDateWithoutTime(row.EffectiveDateOfContract) : '',
        maturityDateOfContract: row.MaturityDateOfContract !== 0 ? _formatDateWithoutTime(row.MaturityDateOfContract) : '',
        spread: row.Spread,
        upfrontPayment: row.UpfrontPayment === 0.0 ? '' : row.UpfrontPayment,
        clearingHouseLEI: row.ClearingHouseLEI,
        isActual: row.isActual,
        tradingSystem: showNotAvailableIfEmpty(row.TradingSystem, row, order)
      }))
    );
  }
};

class MultipleOrderAdapter extends OrderService {
  loadData(criteria, customErrorHandler) {
    return super.loadData(criteria, customErrorHandler).then(function (resp) {
      // Check if `resp` is an array (multiple results) or an object (single result)
      const isMultipleResults = Array.isArray(resp);

      const processSingleView = (r) => {
        const additionalDescription = r.AdditionalDescription || {};

        return {
          data: _transformers[r.ViewType](r.Data || [], r),

          dataType: (r.DataType || '').toLowerCase(),
          viewType: r.ViewType,
          messageType: r.MessageType,
          tradingVenue: r.TradingVenue,
          assetClass: r.AssetClass,
          subAssetClass: r.SubAssetClass,
          instrumentId: r.InstrumentID,
          instrumentDesc: r.InstrumentDescription,
          venueMIC: r.VenueMIC,
          currency: r.Currency,
          venueLocation: r.VenueLocation,

          additionalDescription: {
            instrumentIdType: additionalDescription.InstrumentIDType,
            instrumentId: additionalDescription.InstrumentID,
            displayDate: _formatDate(additionalDescription.Date, "UTC:yyyy-mm-dd'T'HH:MM:ss'Z'")
          }
        };
      };

      if (isMultipleResults) {
        // Multiple views: Map each view using `processSingleView`
        return resp.map((result) => processSingleView(result));
      } else {
        // Single result: Process directly using `processSingleView`
        return processSingleView(resp);
      }
    });
  }
}

export default new MultipleOrderAdapter();


// WEBPACK FOOTER //
// ./src/adapters/MultipleOrderAdapter.js